import { Box, Group, Stack, Text } from "@mantine/core";
import { useDroppable } from "@dnd-kit/core";
import { useTranslation } from "react-i18next";

import DataFieldChip from "./DataFieldChip";
import { DROP_DATA_FIELDS_TARGET, dataFieldsTargetId } from "../../dnd/kinds";
import classes from "./DataFieldsDropTarget.module.scss";

/**
 * The "Data fields" section of a query. Drop an entity-field here to
 * append it (postDataField). Existing chips are re-orderable by dragging
 * onto a sibling chip — the target chip highlights (isOver) instead of
 * animating a "make room" reflow, since that animation doesn't track well
 * for variable-width chips wrapping across rows.
 *
 * The drop-from-outside semantics live in the central handleDragEnd —
 * this component just registers the wrapper droppable and renders the
 * existing chips.
 *
 * NOTE: The legacy code paired each chip with a DataFieldFunctions
 * popover from pages/dataFieldFunctons. That component still depends on
 * the contexts we removed; it gets rewired in PR 4 cleanup. Until then,
 * compound function attachment on data fields is unavailable here.
 */
export default function DataFieldsDropTarget({
  ruleId,
  queryId,
  dataFields,
  canEdit = true,
}) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({
    id: dataFieldsTargetId(queryId),
    data: { kind: DROP_DATA_FIELDS_TARGET, queryId },
  });

  return (
    <Stack gap={4}>
      <Text size="sm" fw={600}>
        {t("dataFields.dataFields")}
      </Text>
      <Box
        ref={setNodeRef}
        className={`${classes.wrapper} ${isOver ? classes.over : ""}`}
      >
        {!dataFields?.length ? (
          <Text size="sm" c="dimmed" ta="center" py="sm">
            {t("dataFields.dropDataField")}
          </Text>
        ) : (
          <Group gap={6} className={classes.list}>
            {dataFields.map((field, i) => (
              <DataFieldChip
                key={field.id}
                ruleId={ruleId}
                queryId={queryId}
                dataField={field}
                index={i}
                canEdit={canEdit}
              />
            ))}
          </Group>
        )}
      </Box>
    </Stack>
  );
}
